import { useCallback, useEffect, useRef, useState } from "react";
import { Download, Eye, FileText, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  despachoFiscalInfoApi,
  parseFiscalInfoError,
} from "@/modules/despacho/despachoFiscalInfoApi";
import { shouldAutoSyncCsfDetail, csfSyncAppliedCount } from "@/modules/despacho/despachoFiscalInfoHelpers";

function csfSourceLabel(source) {
  const raw = String(source || "").trim();
  if (!raw) return "—";
  if (raw === "manual" || raw.includes("manual")) return "Carga manual";
  if (raw.includes("sat")) return "Descarga SAT";
  return raw;
}

/**
 * Pestaña Constancia fiscal en Editar cliente — reutiliza API despacho (sin SAT/XML).
 */
export default function ClientCsfTabPanel({
  clientId,
  readOnly = false,
  onApplyParsedFields,
}) {
  const fileInputRef = useRef(null);
  const [loadState, setLoadState] = useState("loading");
  const [csfMeta, setCsfMeta] = useState({});
  const [lastParsed, setLastParsed] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState("");

  const loadMeta = useCallback(async ({ skipAutoSync = false } = {}) => {
    if (!clientId) return;
    setLoadState("loading");
    try {
      let data = await despachoFiscalInfoApi.getClientDetail(clientId, { autoSyncCsf: false });
      if (!readOnly && !skipAutoSync && shouldAutoSyncCsfDetail(data)) {
        try {
          await despachoFiscalInfoApi.syncCsfInternal(clientId);
          data = await despachoFiscalInfoApi.getClientDetail(clientId, { autoSyncCsf: false });
        } catch {
          // conservar detalle parcial si falla sync
        }
      }
      setCsfMeta(data?.csf_meta || {});
      setLoadState("ready");
    } catch (err) {
      setCsfMeta({});
      setLoadState(parseFiscalInfoError(err).kind === "forbidden" ? "forbidden" : "error");
    }
  }, [clientId, readOnly]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  const handleViewPdf = async () => {
    if (!clientId || !csfMeta?.has_csf) return;
    setPdfBusy("view");
    try {
      await despachoFiscalInfoApi.openCsfPdf(clientId);
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setPdfBusy("");
    }
  };

  const handleDownloadPdf = async () => {
    if (!clientId || !csfMeta?.has_csf) return;
    setPdfBusy("download");
    try {
      await despachoFiscalInfoApi.openCsfPdf(clientId, { download: true });
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setPdfBusy("");
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !clientId || readOnly) return;
    setUploadBusy(true);
    try {
      const data = await despachoFiscalInfoApi.uploadCsf(clientId, file);
      const parsed = data?.parsed || null;
      setLastParsed(parsed);
      let added = data?.suggested_added ?? 0;
      const detected = data?.obligaciones_detectadas?.length ?? 0;
      if (added === 0 && detected > 0 && !readOnly) {
        try {
          const syncResult = await despachoFiscalInfoApi.syncCsfInternal(clientId);
          added = csfSyncAppliedCount(syncResult);
        } catch {
          // PDF guardado; usuario puede sincronizar en Información fiscal
        }
      }
      // Al subir la CSF, completar régimen / CP / dirección / CURP en el formulario de Datos.
      if (parsed && onApplyParsedFields) {
        onApplyParsedFields(parsed);
      }
      const filled = [
        parsed?.regimen_fiscal ? "régimen" : null,
        parsed?.codigo_postal ? "código postal" : null,
        parsed?.direccion ? "dirección" : null,
        parsed?.curp ? "CURP" : null,
      ].filter(Boolean);
      if (added > 0) {
        toast.success(
          filled.length
            ? `Constancia guardada: ${added} obligación(es) y datos aplicados (${filled.join(", ")}). Revise y guarde.`
            : `Constancia guardada: ${added} obligación(es) activada(s) en despacho.`,
        );
      } else if (filled.length) {
        toast.success(
          `Constancia guardada. Se aplicaron ${filled.join(", ")} al formulario; revise Datos del cliente y guarde.`,
        );
      } else if (detected > 0) {
        toast.message("Constancia guardada. Revise obligaciones en Clientes → Obligaciones fiscales.");
      } else {
        toast.success("Constancia guardada en el expediente del cliente.");
      }
      await loadMeta({ skipAutoSync: true });
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setUploadBusy(false);
    }
  };

  const handleApplyToForm = () => {
    if (!lastParsed || !onApplyParsedFields) return;
    onApplyParsedFields(lastParsed);
    toast.success("Datos del PDF aplicados al formulario. Revise la pestaña Datos del cliente y guarde.");
  };

  if (loadState === "loading") {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-600 py-6">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        Cargando constancia…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="client-dialog-csf-tab">
      <p className="text-[11px] text-slate-600 m-0 leading-snug">
        Suba la <strong>Constancia de Situación Fiscal</strong> (PDF del SAT). Se guarda una copia en el expediente
        del cliente y alimenta el perfil fiscal del despacho (régimen y obligaciones).{" "}
        <strong>No activa descargas SAT ni el robot de XML.</strong>
      </p>

      <div className="rounded-sm border border-slate-200 bg-slate-50/80 px-3 py-3 text-xs text-slate-700 space-y-1">
        <p className="m-0 flex items-center gap-1.5">
          <FileText className="h-3.5 w-3.5 shrink-0 text-slate-500" aria-hidden />
          <strong>Estado:</strong> {csfMeta.has_csf ? "Constancia disponible" : "Sin constancia cargada"}
        </p>
        {csfMeta.source ? (
          <p className="m-0">
            <strong>Origen:</strong> {csfSourceLabel(csfMeta.source)}
          </p>
        ) : null}
        {csfMeta.uploaded_at ? (
          <p className="m-0">
            <strong>Última carga:</strong> {String(csfMeta.uploaded_at).slice(0, 10)}
          </p>
        ) : null}
        {csfMeta.synced_at ? (
          <p className="m-0">
            <strong>Obligaciones en catálogo:</strong> {csfMeta.obligaciones_matched_count ?? 0}
            {(csfMeta.obligaciones_detectadas_count ?? 0) > 0
              ? ` · ${csfMeta.obligaciones_detectadas_count} detectada(s) en PDF`
              : ""}
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-sm h-8"
          disabled={!!pdfBusy || !csfMeta.has_csf}
          onClick={handleViewPdf}
          data-testid="client-csf-view-pdf"
        >
          {pdfBusy === "view" ? (
            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" aria-hidden />
          ) : (
            <Eye className="h-3.5 w-3.5 mr-1" aria-hidden />
          )}
          Ver PDF
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-sm h-8"
          disabled={!!pdfBusy || !csfMeta.has_csf}
          onClick={handleDownloadPdf}
          data-testid="client-csf-download-pdf"
        >
          {pdfBusy === "download" ? (
            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" aria-hidden />
          ) : (
            <Download className="h-3.5 w-3.5 mr-1" aria-hidden />
          )}
          Descargar copia
        </Button>
        {!readOnly ? (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={handleUpload}
              data-testid="client-csf-upload-input"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-sm h-8 border-[#0F2D5C] text-[#0F2D5C]"
              disabled={uploadBusy}
              onClick={() => fileInputRef.current?.click()}
              data-testid="client-csf-upload-btn"
            >
              {uploadBusy ? (
                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" aria-hidden />
              ) : (
                <Upload className="h-3.5 w-3.5 mr-1" aria-hidden />
              )}
              {csfMeta.has_csf ? "Reemplazar PDF" : "Subir constancia (PDF)"}
            </Button>
          </>
        ) : null}
      </div>

      {lastParsed && onApplyParsedFields && !readOnly ? (
        <div className="rounded-sm border border-emerald-200 bg-emerald-50/80 px-3 py-2 text-xs text-emerald-950 space-y-2">
          <p className="m-0">
            Al subir el PDF se aplican automáticamente <strong>régimen fiscal</strong>,{" "}
            <strong>código postal</strong> y <strong>dirección</strong> a la pestaña Datos del cliente.
            Puede volver a aplicarlos si editó el formulario.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-sm h-8 bg-white"
            onClick={handleApplyToForm}
            data-testid="client-csf-apply-to-form"
          >
            Reaplicar datos al formulario
          </Button>
        </div>
      ) : null}
    </div>
  );
}
